import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  getUserDisplaySettings,
  createUserDisplaySetting,
  updateUserDisplaySetting,
  deleteUserDisplaySetting,
} from "@/api/user-display-settings-api";
import {
  DEFAULT_TABS,
  DEFAULT_COLUMNS,
  MEASUREMENT_COLUMNS,
  ASSET_PROPERTY_NAMES,
  makeColumnKey,
} from "@/constants/operating-points-constants";
import { isRequestAborted } from "@/api/api-utils.js";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import {
  buildOperatingPointsViewsStateFromUserDisplaySettings,
  findOperatingPointsTabIdForViewName,
  getUserDisplaySettingsListFromApiResponse,
} from "@/lib/operating-points-display-settings.js";

/**
 * Measurement columns only (no fixed asset columns). Drop any column not tied to a
 * UniversalDescriptorId (tag-name-only measurement columns are not supported).
 * @param {Array} cols
 * @returns {Array}
 */
function applyOperatingPointsMeasurementFilters(cols) {
  const base = cols.filter(
    (col) => !col.tagName || !ASSET_PROPERTY_NAMES.has(col.tagName)
  );
  return base.filter(
    (col) =>
      col.universalDescriptorId != null
      && Number.isFinite(Number(col.universalDescriptorId))
  );
}

/**
 * @typedef {{ universalDescriptorId?: number|null }} ColumnUnitInput
 */

/**
 * Custom hook to manage views and columns for operating points page
 * Handles CRUD operations, caching, and state management
 * @param {(input: ColumnUnitInput) => string} getColumnUnit - Resolve display unit from UniversalDescriptorId
 * @returns {Object} Views and columns state and handlers
 */
export function useOperatingPointsViews(getColumnUnit) {
  const { tenantRefreshKey } = useSessionTenant();

  // Views state
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [customColumns, setCustomColumns] = useState({});
  const [savedColumns, setSavedColumns] = useState({});
  const [replacedDefaultTabs, setReplacedDefaultTabs] = useState({});
  /** @type {Object.<string, number>} Maps tab id (`custom-{AppUserDisplaySettingsId}`) → backend id */
  const [viewNameToBackendId, setViewNameToBackendId] = useState({});

  // Loading states
  const [isLoadingViews, setIsLoadingViews] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Cache for user display settings to avoid refetching on every mount
  const viewsCacheRef = useRef(
    /** @type {{tabs: Array, customColumns: Object, savedColumns: Object, viewNameToBackendId: Object, lastFetched: number}|null} */ (
      null
    )
  );
  const hasLoadedViewsRef = useRef(false);
  const lastTenantRefreshKeyRef = useRef(0);

  // Helper function to create default view columns for a tab
  const getDefaultViewColumns = useCallback(
    (tabId) => {
      const measurementCols = MEASUREMENT_COLUMNS[tabId] || [];
      return [...DEFAULT_COLUMNS, ...measurementCols];
    },
    [DEFAULT_COLUMNS, MEASUREMENT_COLUMNS]
  );

  // Helper function to extract view ID from API response
  const extractViewId = useCallback((response) => {
    if (!response) return null;

    const paths = [
      "AppUserDisplaySettingsId",
      "appUserDisplaySettingsId",
      "id",
      "appUserDisplaySetting.AppUserDisplaySettingsId",
      "appUserDisplaySetting.appUserDisplaySettingsId",
      "appUserDisplaySetting.id",
      "data.AppUserDisplaySettingsId",
      "data.appUserDisplaySettingsId",
      "data.id",
      "result.AppUserDisplaySettingsId",
      "result.appUserDisplaySettingsId",
      "result.id",
    ];

    for (const path of paths) {
      const value = path.split(".").reduce((obj, key) => obj?.[key], response);
      if (
        value != null
        && (typeof value === "number" || /^\d+$/.test(String(value)))
      ) {
        return typeof value === "number" ? value : parseInt(value, 10);
      }
    }

    // If no ID found, try to recursively search for any numeric ID
    const findNumericId = (obj, depth = 0, visited = new Set()) => {
      if (depth > 3 || visited.has(obj)) return null;
      visited.add(obj);

      if (typeof obj === "number" && obj > 0) return obj;
      if (typeof obj === "string" && /^\d+$/.test(obj)) {
        return parseInt(obj, 10);
      }

      if (typeof obj === "object" && obj !== null) {
        if (Array.isArray(obj)) {
          for (const item of obj) {
            const found = findNumericId(item, depth + 1, visited);
            if (found) return found;
          }
        } else {
          for (const value of Object.values(obj)) {
            const found = findNumericId(value, depth + 1, visited);
            if (found) return found;
          }
        }
      }
      return null;
    };

    return findNumericId(response);
  }, []);

  const clearOperatingPointsViewsState = useCallback(() => {
    setTabs([]);
    setCustomColumns({});
    setSavedColumns({});
    setViewNameToBackendId({});
    viewsCacheRef.current = {
      tabs: [],
      customColumns: {},
      savedColumns: {},
      viewNameToBackendId: {},
      lastFetched: Date.now(),
    };
  }, [setCustomColumns, setSavedColumns, setTabs, setViewNameToBackendId]);

  const applyOperatingPointsViewsState = useCallback(
    (state, { activateTabId } = {}) => {
      const {
        tabs: apiTabs,
        customColumns: cols,
        savedColumns: saved,
        viewNameToBackendId: idMap,
      } = state;

      setReplacedDefaultTabs({});
      setViewNameToBackendId(idMap);
      setTabs(apiTabs);
      setCustomColumns(cols);
      setSavedColumns(saved);
      setActiveTab((current) => {
        if (activateTabId && apiTabs.some((t) => t.id === activateTabId)) {
          return activateTabId;
        }
        if (current && apiTabs.some((t) => t.id === current)) return current;
        return apiTabs[0]?.id ?? null;
      });

      viewsCacheRef.current = {
        tabs: apiTabs,
        customColumns: cols,
        savedColumns: saved,
        viewNameToBackendId: idMap,
        lastFetched: Date.now(),
      };
    },
    [
      setActiveTab,
      setCustomColumns,
      setSavedColumns,
      setTabs,
      setViewNameToBackendId,
    ]
  );

  /** Sync tabs/columns from API response (full UserDisplaySettings list). */
  const applyOperatingPointsViewsFromApiResponse = useCallback(
    (response, options = {}) => {
      const settings = getUserDisplaySettingsListFromApiResponse(response);
      if (!settings?.length) return false;

      const state = buildOperatingPointsViewsStateFromUserDisplaySettings(
        settings,
        getColumnUnit
      );
      if (state.tabs.length === 0) {
        clearOperatingPointsViewsState();
        return false;
      }

      const activateTabId =
        options.activateTabId
        ?? (options.activateTabLabel
          ? findOperatingPointsTabIdForViewName(
              settings,
              options.activateTabLabel
            )
          : undefined);

      applyOperatingPointsViewsState(state, { activateTabId });
      return true;
    },
    [
      applyOperatingPointsViewsState,
      clearOperatingPointsViewsState,
      getColumnUnit,
    ]
  );

  const reloadUserDisplaySettings = useCallback(
    async (forceReload = false) => {
      if (!forceReload && viewsCacheRef.current) {
        const cached = viewsCacheRef.current;
        setTabs(cached.tabs);
        const withTabIds = Object.fromEntries(
          Object.entries(cached.customColumns || {}).map(([tabId, cols]) => [
            tabId,
            (cols || []).map((c) => ({ ...c, _tabId: c._tabId || tabId })),
          ])
        );
        setCustomColumns(withTabIds);
        setSavedColumns(cached.savedColumns);
        setViewNameToBackendId(cached.viewNameToBackendId);
        if (cached.tabs.length > 0 && !activeTab) {
          setActiveTab(cached.tabs[0].id);
        }
        setIsLoadingViews(false);
        return;
      }

      try {
        setIsLoadingViews(true);

        const response = await getUserDisplaySettings("OperatingPoints");

        let userDisplaySettings = null;
        if (response && response.UserDisplaySettings) {
          userDisplaySettings = response.UserDisplaySettings;
        } else if (Array.isArray(response)) {
          userDisplaySettings = response;
        } else if (
          response
          && response.data
          && response.data.UserDisplaySettings
        ) {
          userDisplaySettings = response.data.UserDisplaySettings;
        }

        // If user has no views, create default views in the backend
        if (
          !userDisplaySettings
          || !Array.isArray(userDisplaySettings)
          || userDisplaySettings.length === 0
        ) {
          const defaultViewPromises = DEFAULT_TABS.map(async (tab) => {
            const defaultColumns = getDefaultViewColumns(tab.id);
            const columnsForApi = defaultColumns.map((col) => ({
              labelName: col.labelName,
              tagName: col.tagName,
              ...(col.universalDescriptorId != null
                ? { universalDescriptorId: col.universalDescriptorId }
                : {}),
            }));
            const jsonString = JSON.stringify(columnsForApi);

            const requestBody = {
              PageName: "OperatingPoints",
              UserDisplaySettingsName: tab.label,
              UserDisplaySettingsData: jsonString,
            };

            const createResponse = await createUserDisplaySetting(requestBody);

            return createResponse;
          });

          await Promise.all(defaultViewPromises);

          // Reload settings after creating defaults
          const reloadResponse =
            await getUserDisplaySettings("OperatingPoints");

          if (reloadResponse && reloadResponse.UserDisplaySettings) {
            userDisplaySettings = reloadResponse.UserDisplaySettings;
          } else if (Array.isArray(reloadResponse)) {
            userDisplaySettings = reloadResponse;
          } else if (reloadResponse?.data?.UserDisplaySettings) {
            userDisplaySettings = reloadResponse.data.UserDisplaySettings;
          }
        }

        if (userDisplaySettings && Array.isArray(userDisplaySettings)) {
          const state = buildOperatingPointsViewsStateFromUserDisplaySettings(
            userDisplaySettings,
            getColumnUnit
          );
          if (state.tabs.length > 0) {
            applyOperatingPointsViewsState(state);
          } else {
            clearOperatingPointsViewsState();
          }
        } else {
          clearOperatingPointsViewsState();
        }
      } catch (error) {
        if (isRequestAborted(error)) {
          return;
        }
        console.error("reloadUserDisplaySettings - ERROR", {
          error: error.message,
          errorResponse: error.response?.data,
        });
        if (!forceReload) {
          clearOperatingPointsViewsState();
        }
      } finally {
        setIsLoadingViews(false);
      }
    },
    [
      applyOperatingPointsViewsState,
      clearOperatingPointsViewsState,
      getDefaultViewColumns,
      activeTab,
      DEFAULT_TABS,
      DEFAULT_COLUMNS,
      getColumnUnit,
    ]
  );

  // Handle restoring default views - deletes all existing views and creates the 3 default views
  const handleRestoreDefaultViews = useCallback(async () => {
    try {
      setIsLoadingViews(true);

      // First, get all existing views
      const response = await getUserDisplaySettings("OperatingPoints");
      let userDisplaySettings = null;
      if (response && response.UserDisplaySettings) {
        userDisplaySettings = response.UserDisplaySettings;
      } else if (Array.isArray(response)) {
        userDisplaySettings = response;
      } else if (response?.data?.UserDisplaySettings) {
        userDisplaySettings = response.data.UserDisplaySettings;
      }

      // Delete all existing views
      if (userDisplaySettings && Array.isArray(userDisplaySettings)) {
        const deletePromises = userDisplaySettings.map(async (setting) => {
          const settingId =
            setting.AppUserDisplaySettingsId
            || setting.appUserDisplaySettingsId
            || setting.id;
          if (settingId) {
            try {
              await deleteUserDisplaySetting(settingId);
            } catch (error) {
              // Continue even if some deletions fail
              console.warn(`Failed to delete view ${settingId}:`, error);
            }
          }
        });
        await Promise.all(deletePromises);
      }

      // Now create the 3 default views
      const defaultViewPromises = DEFAULT_TABS.map(async (tab) => {
        const defaultColumns = getDefaultViewColumns(tab.id);
        const columnsForApi = defaultColumns.map((col) => ({
          labelName: col.labelName,
          tagName: col.tagName,
          unit: col.unit || "",
          ...(col.universalDescriptorId != null
            ? { universalDescriptorId: col.universalDescriptorId }
            : {}),
        }));
        const jsonString = JSON.stringify(columnsForApi);

        return createUserDisplaySetting({
          PageName: "OperatingPoints",
          UserDisplaySettingsName: tab.label,
          UserDisplaySettingsData: jsonString,
        });
      });

      await Promise.all(defaultViewPromises);

      // Clear cache and force reload to show the new default views
      viewsCacheRef.current = null;
      await reloadUserDisplaySettings(true);
    } catch (error) {
      console.error("Failed to restore default views:", error);
      // Still try to reload in case some views were created
      viewsCacheRef.current = null;
      await reloadUserDisplaySettings(true);
    } finally {
      setIsLoadingViews(false);
    }
  }, [getDefaultViewColumns, reloadUserDisplaySettings, DEFAULT_TABS]);

  // Handle creating a new view/tab
  const handleCreateView = useCallback(
    async (viewName) => {
      try {
        setIsCreating(true);
        const jsonString = JSON.stringify(DEFAULT_COLUMNS);

        const requestBody = {
          PageName: "OperatingPoints",
          UserDisplaySettingsName: viewName,
          UserDisplaySettingsData: jsonString,
        };

        // Call API to create the view
        const response = await createUserDisplaySetting(requestBody);

        const applied = applyOperatingPointsViewsFromApiResponse(response, {
          activateTabLabel: viewName,
        });
        if (!applied) {
          console.error("Create view: response missing UserDisplaySettings", {
            viewName,
            response,
          });
          throw new Error(
            `Failed to create view: API response missing settings list. Response: ${JSON.stringify(response)}`
          );
        }
      } catch (error) {
        // Re-throw the error so the dialog can handle it
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [applyOperatingPointsViewsFromApiResponse, setIsCreating]
  );

  // Handle deleting the active tab
  const handleDeleteView = useCallback(async () => {
    if (!activeTab) {
      return;
    }

    try {
      setIsDeleting(true);

      const userDisplaySettingsId = viewNameToBackendId[activeTab];

      if (userDisplaySettingsId) {
        await deleteUserDisplaySetting(userDisplaySettingsId);
      }

      // Remove the tab and columns
      const newTabs = tabs.filter((tab) => tab.id !== activeTab);
      if (newTabs.length > 0) {
        setTabs(newTabs);
        // Switch to the first available tab
        setActiveTab(newTabs[0].id);
      } else {
        // If no tabs remain, show empty state (user deleted all views)
        setTabs([]);
      }

      // Remove columns for the deleted tab
      setCustomColumns((prev) => {
        const updated = { ...prev };
        delete updated[activeTab];
        return updated;
      });
      // Remove saved columns for the deleted tab
      setSavedColumns((prev) => {
        const updated = { ...prev };
        delete updated[activeTab];
        return updated;
      });

      setViewNameToBackendId((prev) => {
        const updated = { ...prev };
        delete updated[activeTab];
        if (viewsCacheRef.current) {
          viewsCacheRef.current = {
            tabs: newTabs.length > 0 ? newTabs : [],
            customColumns: { ...viewsCacheRef.current.customColumns },
            savedColumns: { ...viewsCacheRef.current.savedColumns },
            viewNameToBackendId: updated,
            lastFetched: Date.now(),
          };
          delete viewsCacheRef.current.customColumns[activeTab];
          delete viewsCacheRef.current.savedColumns[activeTab];
        }
        return updated;
      });
    } catch (error) {
      // no-op: keep tab on delete failure
    } finally {
      setIsDeleting(false);
    }
  }, [
    activeTab,
    tabs,
    viewNameToBackendId,
    setActiveTab,
    setCustomColumns,
    setIsDeleting,
    setSavedColumns,
    setTabs,
    setViewNameToBackendId,
    viewsCacheRef,
  ]);

  const handleAddColumn = useCallback(
    ({ displayName, universalDescriptorId }) => {
      if (!displayName || !activeTab) return;
      const udRaw = universalDescriptorId;
      const udId =
        udRaw != null && udRaw !== ""
          ? Number.parseInt(String(udRaw), 10)
          : NaN;
      if (!Number.isFinite(udId)) return;

      const newColumn = {
        key: makeColumnKey(displayName, "", udId),
        labelName: displayName,
        unit: getColumnUnit({
          tagName: "",
          universalDescriptorId: udId,
        }),
        tagName: "",
        universalDescriptorId: udId,
        _tabId: activeTab,
      };

      setCustomColumns((prev) => ({
        ...prev,
        [activeTab]: [...(prev[activeTab] || []), newColumn],
      }));
    },
    [activeTab, getColumnUnit, setCustomColumns]
  );

  const handleUpdateColumn = useCallback(
    ({ displayName, universalDescriptorId }, editingColumn) => {
      if (!editingColumn || !displayName || !activeTab) return;

      const udRaw = universalDescriptorId;
      const udId =
        udRaw != null && udRaw !== ""
          ? Number.parseInt(String(udRaw), 10)
          : NaN;
      if (!Number.isFinite(udId)) return;

      const tagChanged = editingColumn.universalDescriptorId !== udId;
      const unit = tagChanged
        ? getColumnUnit({
            tagName: "",
            universalDescriptorId: udId,
          })
        : editingColumn.unit || "";

      const key = makeColumnKey(displayName, "", udId);
      const oldKey = editingColumn.key;

      const newColumn = {
        key,
        labelName: displayName,
        unit,
        tagName: "",
        universalDescriptorId: udId,
        _tabId: activeTab,
      };

      setCustomColumns((prev) => {
        const updated = { ...prev };
        const tabColumns = [...(updated[activeTab] || [])];
        const oldIndex = tabColumns.findIndex((col) => col.key === oldKey);
        if (oldIndex === -1) return prev;

        const replaced = tabColumns.map((col, i) => {
          if (i === oldIndex) return newColumn;
          if (col.key === key) {
            return { ...col, key: `${key}-${col.key}` };
          }
          return col;
        });

        updated[activeTab] = replaced;
        return updated;
      });
    },
    [activeTab, getColumnUnit, setCustomColumns]
  );

  const handleDeleteColumn = useCallback(
    (editingColumn) => {
      if (!editingColumn || !activeTab) return;

      setCustomColumns((prev) => {
        const updated = { ...prev };
        const tabColumns = [...(updated[activeTab] || [])];
        const filtered = tabColumns.filter(
          (col) => col.key !== editingColumn.key
        );
        updated[activeTab] = filtered;
        return updated;
      });
    },
    [activeTab, setCustomColumns]
  );

  const mergeMeasurementColumns = useCallback(
    (tabId, defaultMeasurementCols, customCols) => {
      if (tabId.startsWith("custom-")) {
        return applyOperatingPointsMeasurementFilters(customCols);
      }
      const defaultKeys = new Set(defaultMeasurementCols.map((col) => col.key));
      const customKeys = new Set(customCols.map((col) => col.key));
      const hasAllDefaults = defaultMeasurementCols.every((col) =>
        customKeys.has(col.key)
      );

      if (
        hasAllDefaults
        && customCols.length >= defaultMeasurementCols.length
      ) {
        return applyOperatingPointsMeasurementFilters(
          customCols.filter(
            (col) =>
              (!col.tagName || !ASSET_PROPERTY_NAMES.has(col.tagName))
              && (defaultKeys.has(col.key) || !defaultKeys.has(col.key))
          )
        );
      }
      const customMap = new Map(customCols.map((col) => [col.key, col]));
      const merged = defaultMeasurementCols.map((col) => {
        return customMap.has(col.key) ? customMap.get(col.key) : col;
      });
      customCols.forEach((col) => {
        if (!defaultMeasurementCols.some((dc) => dc.key === col.key)) {
          merged.push(col);
        }
      });
      return applyOperatingPointsMeasurementFilters(merged);
    },
    [ASSET_PROPERTY_NAMES]
  );

  const handleMoveColumnLeft = useCallback(
    (column, currentIndex) => {
      if (!activeTab || currentIndex === 0) return;

      const defaultMeasurementCols = activeTab.startsWith("custom-")
        ? []
        : MEASUREMENT_COLUMNS[activeTab] || [];
      const currentCustom = customColumns[activeTab] || [];
      const customMap = new Map(currentCustom.map((col) => [col.key, col]));
      const merged = defaultMeasurementCols.map((col) => {
        return customMap.has(col.key) ? customMap.get(col.key) : col;
      });
      currentCustom.forEach((col) => {
        if (!defaultMeasurementCols.some((dc) => dc.key === col.key)) {
          merged.push(col);
        }
      });
      const filtered = applyOperatingPointsMeasurementFilters(merged);

      const colIndex = filtered.findIndex((col) => col.key === column.key);
      if (colIndex > 0) {
        const reordered = [...filtered];
        [reordered[colIndex - 1], reordered[colIndex]] = [
          reordered[colIndex],
          reordered[colIndex - 1],
        ];
        setCustomColumns((prev) => ({
          ...prev,
          [activeTab]: reordered,
        }));
      }
    },
    [
      activeTab,
      customColumns,
      MEASUREMENT_COLUMNS,
      ASSET_PROPERTY_NAMES,
      setCustomColumns,
    ]
  );

  const handleMoveColumnRight = useCallback(
    (column, currentIndex) => {
      if (!activeTab) return;

      const defaultMeasurementCols = activeTab.startsWith("custom-")
        ? []
        : MEASUREMENT_COLUMNS[activeTab] || [];
      const currentCustom = customColumns[activeTab] || [];
      const customMap = new Map(currentCustom.map((col) => [col.key, col]));
      const merged = defaultMeasurementCols.map((col) => {
        return customMap.has(col.key) ? customMap.get(col.key) : col;
      });
      currentCustom.forEach((col) => {
        if (!defaultMeasurementCols.some((dc) => dc.key === col.key)) {
          merged.push(col);
        }
      });
      const filtered = applyOperatingPointsMeasurementFilters(merged);

      const colIndex = filtered.findIndex((col) => col.key === column.key);
      if (colIndex < filtered.length - 1) {
        const reordered = [...filtered];
        [reordered[colIndex], reordered[colIndex + 1]] = [
          reordered[colIndex + 1],
          reordered[colIndex],
        ];
        setCustomColumns((prev) => ({
          ...prev,
          [activeTab]: reordered,
        }));
      }
    },
    [
      activeTab,
      customColumns,
      MEASUREMENT_COLUMNS,
      ASSET_PROPERTY_NAMES,
      setCustomColumns,
    ]
  );

  useEffect(() => {
    if (!hasLoadedViewsRef.current) {
      hasLoadedViewsRef.current = true;
      if (!viewsCacheRef.current) {
        reloadUserDisplaySettings(true);
      } else {
        reloadUserDisplaySettings(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      tenantRefreshKey === 0
      || tenantRefreshKey === lastTenantRefreshKeyRef.current
    ) {
      return;
    }
    lastTenantRefreshKeyRef.current = tenantRefreshKey;
    viewsCacheRef.current = null;
    reloadUserDisplaySettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per tenantRefreshKey only
  }, [tenantRefreshKey]);

  // On tab switch, keep only columns for this tab and clone to avoid shared refs.
  useEffect(() => {
    if (!activeTab) return;
    setCustomColumns((prev) => {
      const tabCols = prev[activeTab] || [];
      const kept = tabCols.filter((col) => col._tabId === activeTab);
      if (kept.length === tabCols.length) return prev;
      const newArr = kept.map((c) => ({ ...c }));
      return { ...prev, [activeTab]: newArr };
    });
  }, [activeTab, setCustomColumns]);

  return {
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    customColumns,
    setCustomColumns,
    savedColumns,
    setSavedColumns,
    replacedDefaultTabs,
    setReplacedDefaultTabs,
    viewNameToBackendId,
    setViewNameToBackendId,
    isLoadingViews,
    isSaving,
    isDeleting,
    isCreating,
    setIsSaving,
    setIsDeleting,
    setIsCreating,
    reloadUserDisplaySettings,
    applyOperatingPointsViewsFromApiResponse,
    handleRestoreDefaultViews,
    getDefaultViewColumns,
    extractViewId,
    viewsCacheRef,
    handleCreateView,
    handleDeleteView,
    handleAddColumn,
    handleUpdateColumn,
    handleDeleteColumn,
    handleMoveColumnLeft,
    handleMoveColumnRight,
    mergeMeasurementColumns,
  };
}
